import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import '../styles/CoriBatchExportModal.css';

type Candidate = {
  teacherId: string;
  teacherName: string;
  teacherEmail: string;
  state: 'MA';
  notApproved: boolean;
  noUploadedDocument: boolean;
  eligible: boolean;
  subjectCount: number;
  issues: string[];
};

type Props = { onClose: () => void; onExported: (message: string) => void };

const dateStamp = () => new Date().toISOString().slice(0, 10);

const CoriBatchExportModal: React.FC<Props> = ({ onClose, onExported }) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await httpsCallable(getFunctions(), 'hiringAdminListPendingCoriBatchCandidates')({});
        if (!active) return;
        const rows = ((response.data as any)?.candidates || []) as Candidate[];
        setCandidates(rows);
        setSelected(new Set(rows.filter(row => row.eligible).map(row => row.teacherId)));
      } catch (nextError: any) {
        if (active) setError(nextError?.message || 'Could not load pending CORI teachers.');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, []);

  const eligible = useMemo(() => candidates.filter(row => row.eligible), [candidates]);
  const selectedRows = useMemo(() => eligible.filter(row => selected.has(row.teacherId)), [eligible, selected]);
  const allSelected = eligible.length > 0 && selectedRows.length === eligible.length;
  const subjectCount = selectedRows.reduce((total, row) => total + row.subjectCount, 0);

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(eligible.map(row => row.teacherId)));
  const toggleOne = (teacherId: string) => setSelected(current => {
    const next = new Set(current);
    next.has(teacherId) ? next.delete(teacherId) : next.add(teacherId);
    return next;
  });

  const exportSelected = async () => {
    if (!selectedRows.length) return;
    setExporting(true); setError('');
    try {
      const response = await httpsCallable(getFunctions(), 'hiringAdminExportPendingCoriBatch')({ teacherIds: selectedRows.map(row => row.teacherId) });
      const result = response.data as { content: string; subjectCount: number; teacherCount: number; skipped: Array<{ teacherName: string; reasons: string[] }> };
      if (!result.content) throw new Error('The selected teachers are no longer eligible for export. Refresh and try again.');
      const blob = new Blob([result.content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = `overture-icori-pending-${dateStamp()}.txt`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
      const skipped = result.skipped.length ? ` ${result.skipped.length} changed or incomplete record${result.skipped.length === 1 ? ' was' : 's were'} skipped.` : '';
      onExported(`✅ Exported ${result.teacherCount} Massachusetts teacher${result.teacherCount === 1 ? '' : 's'} as ${result.subjectCount} iCORI row${result.subjectCount === 1 ? '' : 's'}.${skipped}`);
      onClose();
    } catch (nextError: any) {
      setError(nextError?.message || 'Could not export the CORI batch.');
    } finally { setExporting(false); }
  };

  return <div className="cori-batch-modal__overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="cori-batch-modal" role="dialog" aria-modal="true" aria-labelledby="cori-batch-title">
      <header><div><span>Massachusetts only</span><h2 id="cori-batch-title">Review pending CORI export</h2><p>Select the teachers to include before downloading the iCORI upload file.</p></div><button type="button" className="cori-batch-modal__close" onClick={onClose} aria-label="Close">×</button></header>
      {error && <div className="cori-batch-modal__error" role="alert">{error}</div>}
      {loading ? <div className="cori-batch-modal__empty">Loading eligible teachers…</div> : candidates.length === 0 ? <div className="cori-batch-modal__empty">No Massachusetts teachers currently meet both conditions: CORI not approved and no result document uploaded.</div> : <>
        <div className="cori-batch-modal__summary"><strong>{selectedRows.length}</strong> teachers selected <span>·</span> <strong>{subjectCount}</strong> upload rows</div>
        <div className="cori-batch-modal__table-wrap"><table><thead><tr><th><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all eligible teachers" /></th><th>Teacher</th><th>State</th><th>CORI approval</th><th>Result document</th><th>Upload rows</th></tr></thead><tbody>{candidates.map(row => <tr key={row.teacherId} className={row.eligible ? '' : 'is-ineligible'}><td><input type="checkbox" checked={selected.has(row.teacherId)} disabled={!row.eligible} onChange={() => toggleOne(row.teacherId)} aria-label={`Include ${row.teacherName}`} /></td><td><strong>{row.teacherName}</strong><small>{row.teacherEmail || 'No email'}</small>{row.issues.length > 0 && <em>{row.issues.join(', ')}</em>}</td><td><span className="cori-batch-modal__pill">MA</span></td><td><span className="cori-batch-modal__reason">✓ Not approved yet</span></td><td><span className="cori-batch-modal__reason">✓ No uploaded document</span></td><td>{row.eligible ? row.subjectCount : 'Needs correction'}</td></tr>)}</tbody></table></div>
      </>}
      <footer><p>Sensitive identity data. Upload the downloaded file only to your organization’s iCORI account, then delete the local copy.</p><div><button type="button" className="is-secondary" onClick={onClose}>Cancel</button><button type="button" className="is-primary" disabled={loading || exporting || selectedRows.length === 0 || subjectCount > 100} onClick={() => void exportSelected()}>{exporting ? 'Preparing export…' : `Export ${selectedRows.length} selected`}</button></div></footer>
    </section>
  </div>;
};

export default CoriBatchExportModal;
